import React from "react";
import { connect } from "react-redux";
import { ReactComponent as RequestIcon } from "icons/request-icon.svg";
import { ReactComponent as SearchIcon } from "icons/search.svg";
import { ReactComponent as ShortListIcon } from "icons/shortlist.svg";
import { limitNotif } from "helpers/notificationHelper";
import { Button } from "newui/components/Button";
import { SearchUpgradeModal } from "components/GuestModal/SearchUpgradeModal";
import { InfoWrapper } from "components/GuestSidebar/styled";
import { ReactComponent as InfoIcon } from "icons/info2.svg";
import { openContactUsPage } from "helpers/linkHelper";
import { useAllSharedProfiles } from "newui/features/Auth/hooks/useAllSharedProfiles";
import { useCurrentUser } from "hooks/auth";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { AppMenuLink } from "./AppMenuLink";
import { AppMenuFooter } from "./AppMenuFooter";
import { IconBox } from "components/IconBox";
import logo from "../assets/logo.png";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { AppMenuItemRfx } from "./AppMenuItem/AppMenuItemRFx";

interface AppMenuGuestProps {
  guest_rfq: { total: number };
  isCollapsed: boolean;
  onNavigate?: () => void;
}

const AppMenuGuestComponent: React.FC<AppMenuGuestProps> = ({
  isCollapsed,
  guest_rfq,
  onNavigate,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { data, isLoading } = useAllSharedProfiles();
  const sharedProfiles = data?.data?.data || [];
  const { currentUser } = useCurrentUser();
  const { logoutUser } = useAuthContext();
  const { guestPaths } = useNavigationPaths();

  const iconColor = isCollapsed ? "#BDD6FF" : "#0045F5";

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      {/* Top — Guest account info (expanded) / small logo (collapsed) */}
      <div className="flex shrink-0 items-center justify-center py-6">
        {!isCollapsed ? (
          <>
            <div className="w-full px-5">
              <div
                className="fadeintext active font-roboto font-medium text-primary-500"
                style={{ fontSize: "16px" }}
              >
                Guest Account
              </div>
              <div
                className="fadeintext active line-clamp-1 overflow-hidden overflow-ellipsis font-roboto font-medium text-primary-900"
                style={{ fontSize: "18px" }}
              >
                {currentUser?.email}
              </div>
            </div>
          </>
        ) : (
          <img
            src={logo}
            alt="Procuracon Logo"
            style={{ width: "40px", height: "40px" }}
          />
        )}
      </div>

      {/* Top nav — scrollable */}
      <div className={`flex-1 overflow-y-auto py-4 ${isCollapsed ? "overflow-x-hidden" : ""}`}>
        {/* RFx — only shown when guest has RFx access */}
        {guest_rfq.total > 0 && (
          <div
            className={
              isCollapsed
                ? "menuleft mb-1 w-full rounded-md hover:bg-white/20"
                : "menuleft group overflow-hidden hover:bg-utility-blue-light-50 focus:bg-white"
            }
          >
            <AppMenuItemRfx isCollapsed={isCollapsed} link={guestPaths.rfx.all} total={limitNotif(guest_rfq.total)} onNavigate={onNavigate} />
          </div>
        )}

        {/* Shared profiles */}
        <div className="pt-8">
          {!isLoading && sharedProfiles.length > 0 && (
            <div className="w-full border-b border-t border-gray-200 px-4 py-2">
              <span className="font-roboto text-sm font-medium text-gray-500">
                Shared Profiles
              </span>
            </div>
          )}
          {!isLoading &&
            sharedProfiles?.map((profile: any) => (
              <AppMenuLink
                key={profile?.id}
                to={`/company/${profile?.company?.id}`}
                onNavigate={onNavigate}
              >
                <div
                  data-testid={`shared-profile-${profile?.id}`}
                  className={`flex w-full cursor-pointer items-center gap-4 py-3 ${isCollapsed
                    ? "menuleft mb-2 mr-3 rounded-full px-2 hover:bg-white/20"
                    : "menuleft group overflow-hidden px-4 hover:bg-utility-blue-light-50 focus:bg-white"
                    }`}
                >
                  <img
                    className={`${isCollapsed ? "h-[40px] w-[40px]" : "h-[48px] w-[48px]"} rounded-full border-2 border-solid object-cover`}
                    src={profile?.company?.logo_path}
                    alt="img"
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = `${process.env.PUBLIC_URL}/img/placeholder-lg.jpg`;
                    }}
                  />
                  <p className="sidemuchild font-roboto font-medium group-hover:text-royal-blue group-focus:text-royal-blue">
                    {profile?.company?.name}
                  </p>
                </div>
              </AppMenuLink>
            ))}
          {!isLoading && sharedProfiles?.length > 2 && (
            <InfoWrapper>
              <InfoIcon />
              <span>
                Your Guest Account is restricted to 3 profiles. To view more
                companies please upgrade your Account.
              </span>
            </InfoWrapper>
          )}
        </div>
      </div>

      {/* Bottom nav — pinned to bottom */}
      <div className="mt-auto shrink-0">
        {/* Search & Compare — locked */}
        <button
          className={
            isCollapsed
              ? "menuleft mb-1 w-full rounded-md hover:bg-white/20"
              : "menuleft group w-full overflow-hidden hover:bg-utility-blue-light-50 focus:bg-white"
          }
          onClick={() => setIsOpen(true)}
          data-testid="search-&-compare"
        >
          <div
            className={isCollapsed ? "flex w-full items-center justify-center" : "flex items-center"}
            style={isCollapsed ? { padding: "12px 0" } : { paddingLeft: "24px", paddingTop: "10px", paddingBottom: "10px" }}
          >
            <div style={isCollapsed ? {} : { marginRight: "16px" }}>
              <IconBox icon={<SearchIcon />} color={iconColor} width="24px" height="24px" />
            </div>
            {!isCollapsed && (
              <span
                className="sidemuchild font-roboto text-gray-500 group-hover:text-royal-blue group-focus:text-royal-blue"
                style={{ fontSize: "16px", lineHeight: "24px", fontWeight: 500 }}
              >
                Search &amp; Compare
              </span>
            )}
          </div>
        </button>

        {/* Shortlist Report — locked */}
        <button
          className={
            isCollapsed
              ? "menuleft mb-1 w-full rounded-md hover:bg-white/20"
              : "menuleft group w-full overflow-hidden hover:bg-utility-blue-light-50 focus:bg-white"
          }
          onClick={() => setIsOpen(true)}
          data-testid="shortlist-report"
        >
          <div
            className={isCollapsed ? "flex w-full items-center justify-center" : "flex items-center"}
            style={isCollapsed ? { padding: "12px 0" } : { paddingLeft: "24px", paddingTop: "10px", paddingBottom: "10px" }}
          >
            <div style={isCollapsed ? {} : { marginRight: "16px" }}>
              <IconBox icon={<ShortListIcon />} color={iconColor} width="24px" height="24px" />
            </div>
            {!isCollapsed && (
              <span
                className="sidemuchild font-roboto text-gray-500 group-hover:text-royal-blue group-focus:text-royal-blue"
                style={{ fontSize: "16px", lineHeight: "24px", fontWeight: 500 }}
              >
                Shortlist Report
              </span>
            )}
          </div>
        </button>
        <div className="fadeintext active my-4 flex items-center justify-center">
          <Button width="90%" onClick={openContactUsPage}>
            Upgrade Account
          </Button>
        </div>

        <AppMenuFooter
          isCollapsed={isCollapsed}
          onLogout={logoutUser}
          showHelp={false}
        />
      </div>

      <SearchUpgradeModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        upgradeAction
      />
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  guest_rfq: state.guest_rfq,
});

export const AppMenuGuest = connect(mapStateToProps, {})(AppMenuGuestComponent);
